import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { map, share, switchMap, tap } from 'rxjs/operators';
import { APIService, KPI } from '../../@core/api';
import { PerimetersService } from './perimeters.service';

export interface PerimeterControlPointsStats extends KPI.Perimeter {
  control_points_stats: KPI.ControlPointsStats;
  sub_perimeters_control_points_stats: PerimeterControlPointsStats[];
}

@Injectable()
export class ControlPointsStatsService {

  public loading$ = new BehaviorSubject<boolean>(true);

  private _perimetersControlPointsStats$: Observable<PerimeterControlPointsStats[]>;
  private _controlPointsStatsByYear$: Observable<KPI.ControlPointsStatsByYear[]>;

  private controlPointsStatsByYearRefresh$ = new BehaviorSubject<void>(null);

  constructor(
    private api: APIService,
    private perimetersService: PerimetersService,
  ) { }

  public get perimetersControlPointsStats$(): Observable<PerimeterControlPointsStats[]> {
    if (!this._perimetersControlPointsStats$) {
      this._perimetersControlPointsStats$ = this.perimetersService.perimeters$
      .pipe(
        tap(() => this.loading$.next(true)),
        switchMap((perimeters) =>
          this.api.roadmap_control_points_stats().pipe(
            map((controlPointsStats) => ({ perimeters, controlPointsStats })),
          ),
        ),
        map(({ perimeters, controlPointsStats}) => {
          return perimeters.map((perimeter) =>
            this.mapPerimetersControlPointsStats(perimeter, controlPointsStats),
          );
        }),
        tap(() => this.loading$.next(false)),
        share(),
      );
    }

    return this._perimetersControlPointsStats$;
  }

  /**
   * Recover statics on control points on each multi-perimeters by year
   */
  public get controlPointsStatsByYear$(): Observable<KPI.ControlPointsStatsByYear[]> {
    this.loading$.next(true);
    if (!this._controlPointsStatsByYear$) {
      this._controlPointsStatsByYear$ = this.controlPointsStatsByYearRefresh$.pipe(
        switchMap(() => this.api.roadmap_control_points_stats_by_year().pipe(
          tap(() => this.loading$.next(false)),
        )),
        share(),
      );
    }

    return this._controlPointsStatsByYear$;
  }

  /**
   * Forces refresh of audits
   */
  public refreshcontrolPointsStatsByYear(): void {
    this.loading$.next(true);
    this.controlPointsStatsByYearRefresh$.next();
  }

  private mapPerimetersControlPointsStats(
    perimeter: KPI.Perimeter,
    controlPointsStats: KPI.ControlPointsStats[],
  ): PerimeterControlPointsStats {
    let controlPointsStatsPerimeter = controlPointsStats.find(
      (controlPointStats) => controlPointStats.perimeter === perimeter.id,
    );

    if (!controlPointsStatsPerimeter && perimeter.sub_perimeters) {
      controlPointsStatsPerimeter = perimeter.sub_perimeters.reduce(
        (acc, subPerimeter) => {
          const controlPointsStatsSubPerimeter = controlPointsStats.find(
            (controlPointStats) => controlPointStats.perimeter === subPerimeter.id,
          );

          if (controlPointsStatsSubPerimeter) {
            return {
              ...acc,
              acknowledged: acc.acknowledged + controlPointsStatsSubPerimeter.acknowledged,
              score: acc.score + controlPointsStatsSubPerimeter.score,
              remaining: acc.remaining + controlPointsStatsSubPerimeter.remaining,
              total: acc.total + controlPointsStatsSubPerimeter.total,
            };
          }

          return acc;
        },
        {
          perimeter: perimeter.id,
          acknowledged: 0,
          score: 0,
          remaining: 0,
          total: 0,
        },
      );
    }

    return {
      ...perimeter,
      control_points_stats: controlPointsStatsPerimeter,
      sub_perimeters_control_points_stats: perimeter.sub_perimeters && perimeter.sub_perimeters.map(
        (subPerimeter) => this.mapPerimetersControlPointsStats(subPerimeter, controlPointsStats),
      ) || [],
    };
  }

}
